import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useDialers } from '@/features/dialers/api';
import { useCreateGroup } from '../api';
import { buildDialerRowsPayload } from '../payload';
import { groupFormSchema, type GroupFormInput } from '../schema';
import { GroupForm } from './GroupForm';

export function CreateGroupPage() {
  const navigate = useNavigate();
  const dialers = useDialers();
  const create = useCreateGroup();

  const form = useForm<GroupFormInput>({
    resolver: zodResolver(groupFormSchema),
    mode: 'onTouched',
    defaultValues: { groupName: '', dialerIds: [] },
  });

  const onSubmit = (input: GroupFormInput) => {
    const options = (dialers.data ?? []).map((d) => ({
      value: String(d.dialerId),
      label: d.dialerName,
    }));
    create.mutate(
      {
        groupName: input.groupName.trim(),
        dialerRows: buildDialerRowsPayload(input.dialerIds, options),
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Group created');
          navigate('/group-list');
        },
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Could not create the group.'),
      },
    );
  };

  return (
    <GroupForm
      mode="create"
      form={form}
      onSubmit={onSubmit}
      isPending={create.isPending}
      pageTitle="Create Group"
      pageDescription="Logical grouping of dialers used to target campaign delivery."
    />
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
